import { deleteDB, openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'nono';
/** Add an upgrade step (and bump this) for each change to the stores below. */
const DB_VERSION = 2;
/** The database before it was started again at version 1; deleted on first open. */
const RETIRED_DB_NAME = 'pixit';

export const SESSION_STORE = 'inProgressPuzzles';
/** A saved game's moves in chunks (see indexed-db-storage), keyed [puzzleId, part, chunk]. */
export const SESSION_MOVES_STORE = 'inProgressMoves';
export const PROGRESS_STORE = 'puzzleProgress';
export const CATALOG_PACKS_STORE = 'catalogPacks';
export const CATALOG_PUZZLES_STORE = 'catalogPuzzles';
export const DOWNLOADED_PACKS_STORE = 'downloadedPacks';
export const DOWNLOADED_PUZZLES_STORE = 'downloadedPuzzles';
export const REPLAYS_STORE = 'replays';
export const REPLAY_SUMMARIES_STORE = 'replaySummaries';
export const DRAFTS_STORE = 'drafts';

/** Fired on window when another tab's older connection holds up a database upgrade. */
export const DB_BLOCKED_EVENT = 'nono-db-blocked';

export function openClientDb(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, DB_VERSION, {
    blocked() {
      // An older version of the app in another tab still has the database
      // open. It closes its connection when it sees this (blocking below).
      globalThis.dispatchEvent?.(new Event(DB_BLOCKED_EVENT));
    },
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        db.createObjectStore(SESSION_STORE, { keyPath: 'puzzleId' });
        db.createObjectStore(PROGRESS_STORE, { keyPath: 'puzzleId' });
        db.createObjectStore(CATALOG_PACKS_STORE);
        db.createObjectStore(CATALOG_PUZZLES_STORE);
        db.createObjectStore(DOWNLOADED_PACKS_STORE, { keyPath: 'packId' });
        db.createObjectStore(DOWNLOADED_PUZZLES_STORE, { keyPath: 'puzzleId' });
        // Summaries are kept apart so listing the history never loads every move log.
        db.createObjectStore(REPLAYS_STORE, { keyPath: 'id' });
        db.createObjectStore(REPLAY_SUMMARIES_STORE, { keyPath: 'id' });
        db.createObjectStore(DRAFTS_STORE, { keyPath: 'id' });
        // What was kept before is gone (anything synced comes back from the account).
        void deleteDB(RETIRED_DB_NAME).catch(() => undefined);
      }
      if (oldVersion < 2) {
        // Saved games' moves in chunks, so a save writes only what changed.
        // Games saved whole before still load (indexed-db-storage).
        db.createObjectStore(SESSION_MOVES_STORE);
      }
    },
  });
}

/** Fired on window when another tab upgraded the database past this version: saving needs a reload. */
export const DB_OUTDATED_EVENT = 'nono-db-outdated';

/**
 * Lazily opens the shared database, retrying after a failed open. When a
 * newer version of the app (another tab) needs to upgrade, the connection is
 * closed so that tab isn't blocked, and the next call reopens it. Reopening
 * at an older version than the upgrade fails, so `DB_OUTDATED_EVENT` asks
 * the player to reload.
 */
export function lazyClientDb(): () => Promise<IDBPDatabase> {
  let dbPromise: Promise<IDBPDatabase> | null = null;
  return () => {
    if (!dbPromise) {
      const current: Promise<IDBPDatabase> = openClientDb()
        .then((db) => {
          const forget = () => {
            if (dbPromise === current) dbPromise = null;
          };
          db.addEventListener('versionchange', (event) => {
            db.close();
            forget();
            const newVersion = (event as IDBVersionChangeEvent).newVersion;
            if (newVersion !== null && newVersion > DB_VERSION) {
              globalThis.dispatchEvent?.(new Event(DB_OUTDATED_EVENT));
            }
          });
          db.addEventListener('close', forget);
          return db;
        })
        .catch((err) => {
          if (dbPromise === current) dbPromise = null;
          throw err;
        });
      dbPromise = current;
    }
    return dbPromise;
  };
}
